import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Button, Chip, CircularProgress, Alert, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Skeleton, Tooltip, IconButton, ToggleButtonGroup, ToggleButton } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { fetchAiMargin } from "../../pages/ai/aiUsageSlice";
import axiosInstance from "../../../services/axiosInstance";
import {
  Brain, TrendingUp, DollarSign, Percent, RefreshCw,
  Gift, Filter, ChevronUp, ChevronDown, AlertTriangle,
} from "lucide-react";

// ── helpers ───────────────────────────────────────────────────────────────────

function fmtUsd(v, decimals = 4) {
  if (v == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD",
    minimumFractionDigits: 2, maximumFractionDigits: decimals,
  }).format(v);
}

function pctChip(pct) {
  if (pct == null) return <Typography sx={{ color: "text.disabled", fontSize: "0.8rem" }}>—</Typography>;
  const color = pct >= 50 ? "#16A34A" : pct >= 20 ? "#CA8A04" : "#DC2626";
  return (
    <Chip label={`${pct.toFixed(1)}%`} size="small"
      sx={{ fontWeight: 700, height: 20, fontSize: "0.72rem", background: alpha(color, 0.1), color }} />
  );
}

const COMPONENT_LABEL = {
  voice_minute: "AI voice minute (event)", telephony: "Telephony (pass-through)", chat_reply: "Chat reply (event)",
  whatsapp_reply: "WhatsApp reply (event)", ingestion: "Ingestion (event)", notify_email: "Email notify (event)",
  notify_whatsapp_template: "WhatsApp template (event)", action_transactional: "Transactional action (event)", ai_generation: "Generation job, cost × margin (event)",
  order_placed: "Order placed (event)",
  llm: "LLM", llm_in: "LLM", llm_out: "LLM",
  stt: "STT", tts: "TTS", embed: "Embed", rerank: "Rerank",
};

// ── Summary KPI card ──────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, icon: Icon, accent = "text.primary" }) {
  return (
    <Box sx={{
      flex: 1, minWidth: 160, p: 2, borderRadius: 2,
      border: "1px solid var(--line-200)",
      background: "var(--surface-card)",
    }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75 }}>
        {Icon && <Icon size={14} color="var(--line-400)" />}
        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.66rem" }}>
          {label}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: "1.4rem", fontWeight: 800, letterSpacing: "-0.03em", color: accent, fontVariantNumeric: "tabular-nums", lineHeight: 1.2 }}>
        {value}
      </Typography>
      {sub && <Typography variant="caption" sx={{ color: "text.secondary", mt: 0.5, display: "block" }}>{sub}</Typography>}
    </Box>
  );
}

// ── Grant Dialog ──────────────────────────────────────────────────────────────

function GrantDialog({ open, onClose, onSuccess }) {
  const [userId,    setUserId]    = useState("");
  const [amount,    setAmount]    = useState("10");
  const [entryType, setEntryType] = useState("allowance_grant");
  const [note,      setNote]      = useState("");
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);

  const handleClose = () => {
    setUserId(""); setAmount("10"); setEntryType("allowance_grant");
    setNote(""); setError(null); onClose();
  };

  const handleSubmit = async () => {
    const uid = parseInt(userId);
    const amt = parseFloat(amount);
    if (!isFinite(uid) || uid <= 0) { setError("Enter a valid tenant User ID."); return; }
    if (!isFinite(amt) || amt <= 0)  { setError("Enter a positive amount."); return; }
    setLoading(true); setError(null);
    try {
      await axiosInstance.post("/ai/admin/wallet/grant", {
        targetUserId: uid, amount: amt, entryType, note: note.trim() || `Manual ${entryType.replace("_", " ")} from the admin console`,
      });
      onSuccess?.();
      handleClose();
    } catch (e) {
      setError(e.response?.data?.message || e.response?.data?.error || "Grant failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3, border: "1px solid var(--line-200)" } }}>
      <DialogTitle sx={{ fontWeight: 700, pb: 1, fontSize: "1rem" }}>Credit AI Wallet</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "12px !important" }}>
        {error && <Alert severity="error" sx={{ fontSize: "0.82rem" }}>{error}</Alert>}
        <TextField label="Tenant User ID" value={userId} type="number"
          onChange={(e) => setUserId(e.target.value)} size="small" fullWidth autoFocus />
        <TextField label="Amount (USD)" value={amount} type="number"
          onChange={(e) => setAmount(e.target.value)} size="small" fullWidth
          InputProps={{ startAdornment: <Typography sx={{ mr: 0.5, color: "text.secondary" }}>$</Typography> }} />
        <TextField label="Type" value={entryType} select size="small" fullWidth
          onChange={(e) => setEntryType(e.target.value)}>
          <MenuItem value="allowance_grant">Allowance Grant</MenuItem>
          <MenuItem value="adjustment">Manual Adjustment</MenuItem>
          <MenuItem value="refund">Refund</MenuItem>
        </TextField>
        <TextField label="Note (why — shown in the ledger)" value={note} size="small" fullWidth multiline rows={2}
          onChange={(e) => setNote(e.target.value)} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={handleClose} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}
          startIcon={loading ? <CircularProgress size={13} /> : <Gift size={13} />}
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}>
          Apply Credit
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Sort helpers ──────────────────────────────────────────────────────────────

function SortTh({ label, field, sort, onSort }) {
  const active = sort.field === field;
  return (
    <TableCell
      sx={{ fontWeight: 700, fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "text.secondary", cursor: "pointer", whiteSpace: "nowrap", userSelect: "none" }}
      onClick={() => onSort(field)}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {label}
        {active
          ? sort.dir === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />
          : <ChevronDown size={12} style={{ opacity: 0.3 }} />}
      </Box>
    </TableCell>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AiAdminUsage() {
  const dispatch = useDispatch();
  const { margin, marginStatus, marginError } = useSelector((s) => s.aiUsage);

  const [from,      setFrom]      = useState("");
  const [to,        setTo]        = useState("");
  const [tenantId,  setTenantId]  = useState("");
  const [grantOpen, setGrantOpen] = useState(false);
  const [sort,      setSort]      = useState({ field: "costUs", dir: "desc" });
  const [group,     setGroup]     = useState("event");   // Part 4: event = the tenant-priced rows only

  const load = useCallback(() => {
    const params = {};
    if (from)     params.from = from;
    if (to)       params.to   = to;
    if (tenantId) params.userId = tenantId;
    params.group = group;
    dispatch(fetchAiMargin(params));
  }, [dispatch, from, to, tenantId, group]);

  useEffect(() => { load(); }, [load]);

  // ── Sorting ───────────────────────────────────────────────────────────────
  const handleSort = (field) => {
    setSort((prev) => ({ field, dir: prev.field === field && prev.dir === "asc" ? "desc" : "asc" }));
  };

  const rows = Array.isArray(margin) ? [...margin] : [];
  rows.sort((a, b) => {
    const mul = sort.dir === "asc" ? 1 : -1;
    return ((a[sort.field] ?? 0) - (b[sort.field] ?? 0)) * mul;
  });

  // ── Totals from backend (or re-derived) ──────────────────────────────────
  // margin state is the `rows` array from the slice; totals come from the endpoint
  // but the slice only stores the rows. Re-derive totals from the current rows.
  const totals = rows.reduce((acc, r) => {
    acc.requests    += r.requests    ?? 0;
    acc.costUs      += r.costUs      ?? 0;
    acc.priceTenant += r.priceTenant ?? 0;
    acc.grossProfit += r.grossProfit ?? 0;
    return acc;
  }, { requests: 0, costUs: 0, priceTenant: 0, grossProfit: 0 });
  totals.marginPct = totals.costUs > 0
    ? +((totals.grossProfit / totals.costUs) * 100).toFixed(2)
    : null;

  const isLoading = marginStatus === "loading";

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", py: 3, px: { xs: 1, md: 2 } }}>

      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Brain size={20} color="#7A3FF2" />
          <Typography sx={{ fontWeight: 800, fontSize: "1.2rem", letterSpacing: "-0.02em" }}>
            AI Platform Usage
          </Typography>
          <Chip label="Super-Admin" size="small"
            sx={{ fontWeight: 700, fontSize: "0.65rem", height: 20, background: alpha("#DC2626", 0.1), color: "#DC2626" }} />
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="outlined" size="small" onClick={() => setGrantOpen(true)}
            startIcon={<Gift size={13} />}
            sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.8rem", borderRadius: 2, borderColor: "var(--line-200)" }}>
            Credit Wallet
          </Button>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={load} sx={{ border: "1px solid var(--line-200)", borderRadius: 1.5 }}>
              <RefreshCw size={14} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 1.5, mb: 2.5, flexWrap: "wrap", alignItems: "flex-end" }}>
        <TextField label="From" type="date" size="small" value={from}
          onChange={(e) => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }} sx={{ width: 160 }} />
        <TextField label="To" type="date" size="small" value={to}
          onChange={(e) => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }} sx={{ width: 160 }} />
        <TextField label="Tenant ID (optional)" size="small" value={tenantId} type="number"
          onChange={(e) => setTenantId(e.target.value)} sx={{ width: 180 }} />
        <Button variant="contained" size="small" onClick={load}
          startIcon={<Filter size={13} />}
          sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.8rem", borderRadius: 2, height: 40 }}>
          Apply
        </Button>
        {(from || to || tenantId) && (
          <Button size="small" onClick={() => { setFrom(""); setTo(""); setTenantId(""); }}
            sx={{ textTransform: "none", fontSize: "0.8rem", color: "text.secondary" }}>
            Clear
          </Button>
        )}
      </Box>

      {/* Error */}
      {marginStatus === "failed" && (
        <Alert severity="error" sx={{ mb: 2 }} icon={<AlertTriangle size={16} />}>
          {marginError || "Failed to load margin data."}
        </Alert>
      )}

      {/* KPI summary row */}
      {isLoading ? (
        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} variant="rounded" height={90} sx={{ flex: 1 }} />)}
        </Box>
      ) : (
        <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
          <KpiCard label="Total Requests" icon={TrendingUp} value={totals.requests.toLocaleString()} />
          <KpiCard label="Provider Cost (us)" icon={DollarSign}
            value={fmtUsd(totals.costUs)}
            accent="#DC2626"
            sub="Our actual spend to providers" />
          <KpiCard label="Tenant Revenue" icon={DollarSign}
            value={fmtUsd(totals.priceTenant)}
            accent="#2563EB"
            sub="What tenants pay" />
          <KpiCard label="Gross Profit" icon={Percent}
            value={fmtUsd(totals.grossProfit)}
            accent={totals.grossProfit >= 0 ? "#16A34A" : "#DC2626"}
            sub={totals.marginPct != null ? `${totals.marginPct.toFixed(1)}% margin` : undefined} />
        </Box>
      )}

      <Divider sx={{ mb: 2.5 }} />

      {/* Per-tenant per-component table */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5, flexWrap: "wrap" }}>
        <ToggleButtonGroup size="small" exclusive value={group} onChange={(_, v) => v && setGroup(v)}>
          <ToggleButton value="event" sx={{ textTransform: "none", fontSize: "0.75rem", px: 1.5 }}>Per event (what tenants pay)</ToggleButton>
          <ToggleButton value="component" sx={{ textTransform: "none", fontSize: "0.75rem", px: 1.5 }}>Per component (what we pay)</ToggleButton>
        </ToggleButtonGroup>
        <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
          {group === "event" ? "Cost includes speculative platform work. AI events are flagged below the margin floor; telephony is pass-through and exempt." : "Components carry price 0 — the event row is priced."}
        </Typography>
      </Box>
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortTh label="Tenant"    field="userId"      sort={sort} onSort={handleSort} />
              <SortTh label="Component" field="component"   sort={sort} onSort={handleSort} />
              <SortTh label="Requests"  field="requests"    sort={sort} onSort={handleSort} />
              <SortTh label="Cost (us)" field="costUs"      sort={sort} onSort={handleSort} />
              <SortTh label="Revenue"   field="priceTenant" sort={sort} onSort={handleSort} />
              <SortTh label="Profit"    field="grossProfit" sort={sort} onSort={handleSort} />
              <SortTh label="Margin %"  field="marginPct"   sort={sort} onSort={handleSort} />
              <SortTh label="Tokens"    field="tokens"      sort={sort} onSort={handleSort} />
              <TableCell sx={{ fontSize: "0.72rem", fontWeight: 700, color: "text.secondary" }}>Floor</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              [1, 2, 3, 4, 5].map((i) => (
                <TableRow key={i}>
                  {[1,2,3,4,5,6,7].map((j) => (
                    <TableCell key={j}><Skeleton variant="text" width={j === 1 ? 40 : 80} /></TableCell>
                  ))}
                </TableRow>
              ))
            )}

            {!isLoading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5, color: "text.secondary", fontSize: "0.85rem" }}>
                  No usage records for the selected filters.
                </TableCell>
              </TableRow>
            )}

            {!isLoading && rows.map((row, i) => {
              const profit = row.grossProfit ?? 0;
              return (
                <TableRow key={i} hover>
                  <TableCell>
                    <Chip label={`#${row.userId}`} size="small"
                      sx={{ fontWeight: 700, fontSize: "0.72rem", height: 20,
                        background: alpha("#7A3FF2", 0.08), color: "#7A3FF2" }} />
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: "0.82rem", fontWeight: 600 }}>
                      {COMPONENT_LABEL[row.component] || row.component}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ fontVariantNumeric: "tabular-nums", fontSize: "0.82rem" }}>
                    {(row.requests ?? 0).toLocaleString()}
                  </TableCell>
                  <TableCell sx={{ fontVariantNumeric: "tabular-nums", fontSize: "0.82rem", color: "#DC2626", fontWeight: 600 }}>
                    {fmtUsd(row.costUs)}
                  </TableCell>
                  <TableCell sx={{ fontVariantNumeric: "tabular-nums", fontSize: "0.82rem", color: "#2563EB", fontWeight: 600 }}>
                    {fmtUsd(row.priceTenant)}
                  </TableCell>
                  <TableCell sx={{ fontVariantNumeric: "tabular-nums", fontSize: "0.82rem",
                    color: profit >= 0 ? "#16A34A" : "#DC2626", fontWeight: 600 }}>
                    {fmtUsd(profit)}
                  </TableCell>
                  <TableCell>{pctChip(row.marginPct)}</TableCell>
                  <TableCell sx={{ fontVariantNumeric: "tabular-nums", fontSize: "0.82rem" }}>{row.tokens ? Math.round(row.tokens).toLocaleString() : "—"}</TableCell>
                  <TableCell>
                    {!row.isEvent ? <span style={{ opacity: 0.5 }}>—</span>
                      : row.floorExempt ? <Chip size="small" label="pass-through" sx={{ height: 20, fontSize: "0.68rem", background: alpha("#2563EB", 0.1), color: "#1D4ED8" }} />
                      : row.belowFloor ? <Chip size="small" label={`below floor · ${row.grossMarginPct ?? "?"}% gross`} sx={{ height: 20, fontSize: "0.68rem", fontWeight: 700, background: alpha("#DC2626", 0.12), color: "#B91C1C" }} />
                      : <Chip size="small" label={`${row.grossMarginPct ?? "?"}% gross`} sx={{ height: 20, fontSize: "0.68rem", background: alpha("#16A34A", 0.1), color: "#15803D" }} />}
                  </TableCell>
                </TableRow>
              );
            })}

            {/* Totals footer */}
            {!isLoading && rows.length > 1 && (
              <TableRow sx={{ background: (t) => alpha(t.palette.primary.main, 0.04) }}>
                <TableCell colSpan={2}>
                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: "text.secondary" }}>
                    TOTAL ({rows.length} rows)
                  </Typography>
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums", fontSize: "0.82rem" }}>
                  {totals.requests.toLocaleString()}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums", fontSize: "0.82rem", color: "#DC2626" }}>
                  {fmtUsd(totals.costUs)}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums", fontSize: "0.82rem", color: "#2563EB" }}>
                  {fmtUsd(totals.priceTenant)}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums", fontSize: "0.82rem",
                  color: totals.grossProfit >= 0 ? "#16A34A" : "#DC2626" }}>
                  {fmtUsd(totals.grossProfit)}
                </TableCell>
                <TableCell>{pctChip(totals.marginPct)}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <GrantDialog
        open={grantOpen}
        onClose={() => setGrantOpen(false)}
        onSuccess={load}
      />
    </Box>
  );
}
